import { FC, useMemo, useRef, useState } from "react";
import {
    Alert,
    Box,
    Button,
    Card,
    CircularProgress,
    FormControl,
    InputLabel,
    MenuItem,
    Select,
    Snackbar,
    Stack,
    TextField,
    Typography,
} from "@mui/material";
import collectionsController from "../../../../../Api/collectionsController";
import { IProducer } from "../../../../../interfaces/IProducer";

interface NewCollectionFormProps {
    producersData: IProducer[] | null;
}

const NewCollectionForm: FC<NewCollectionFormProps> = ({ producersData }) => {
    const [showForm, setShowForm] = useState(false);
    const [name, setName] = useState("");
    const [producerId, setProducerId] = useState<string>("");
    const [image, setImage] = useState<File | null>(null);
    const [loading, setLoading] = useState(false);
    const [snack, setSnack] = useState<{
        open: boolean;
        message: string;
        severity: "success" | "error";
    }>({ open: false, message: "", severity: "success" });
    const fileInputRef = useRef<HTMLInputElement | null>(null);
    const producers = producersData ?? [];
    const isProducersDisabled = !producersData || producers.length === 0;
    const producersPlaceholder =
        producersData === null ? "Ładowanie producentów..." : "Brak producentów";

    const previewUrl = useMemo(
        () => (image ? URL.createObjectURL(image) : ""),
        [image]
    );

    const onPickFile = () => fileInputRef.current?.click();

    const onFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const f = e.target.files?.[0] || null;
        setImage(f || null);
    };

    const onDrop = (e: React.DragEvent<HTMLDivElement>) => {
        e.preventDefault();
        const f = e.dataTransfer.files?.[0];
        if (f) setImage(f);
    };

    const onDragOver = (e: React.DragEvent<HTMLDivElement>) => e.preventDefault();

    const handleSubmit = async () => {
        if (!name.trim() || !image || !producerId) {
            setSnack({
                open: true,
                message: "Podaj nazwę, wybierz producenta i dodaj zdjęcie",
                severity: "error",
            });
            return;
        }

        setLoading(true);
        try {
            const res = await collectionsController.addCollection({
                name: name.trim(),
                producer: producerId, // ważne: wysyłamy identyfikator producenta
                image,
            });

            if (res.ok) {
                setSnack({
                    open: true,
                    message: "Kolekcja dodana",
                    severity: "success",
                });
                setName("");
                setImage(null);
            } else {
                setSnack({
                    open: true,
                    message: res.message || "Nie udało się dodać kolekcji",
                    severity: "error",
                });
            }
        } catch {
            setSnack({
                open: true,
                message: "Błąd podczas dodawania kolekcji",
                severity: "error",
            });
        } finally {
            setLoading(false);
        }
    };

    if (!showForm) {    
        return (
            <Button variant="contained" onClick={() => setShowForm(true)}>
                Dodaj nową kolekcję
            </Button>
        );
    }

    return (
        <Card
            sx={{
                mt: 3,
                mx: "auto",
                p: 3,
                maxWidth: 960,
                display: "flex",
                flexDirection: "column",
                gap: 3,
            }}
        >
            <Button variant="outlined" onClick={() => setShowForm(false)}>
                Anuluj
            </Button>
            <Typography variant="h4" textAlign="center">
                Dodawanie kolekcji
            </Typography>

            {/* Wybór producenta – analogicznie do ProducerComponent */}
            <FormControl fullWidth disabled={isProducersDisabled}>
                <InputLabel id="producer-select-label">Producent</InputLabel>
                <Select
                    labelId="producer-select-label"
                    label="Producent"
                    value={producerId}
                    onChange={(e) => setProducerId(String(e.target.value))}
                    displayEmpty
                >
                    {producers.length > 0 ? (
                        producers.map((p) => (
                            <MenuItem key={p._id} value={p._id}>
                                {p.name}
                            </MenuItem>
                        ))
                    ) : (
                        <MenuItem value="" disabled>
                            {producersPlaceholder}
                        </MenuItem>
                    )}
                </Select>
            </FormControl>

            <Stack direction={{ xs: "column", md: "row" }} spacing={3}>
                <TextField
                    label="Nazwa kolekcji"
                    value={name}
                    onChange={(e) => setName(e.target.value.toUpperCase())}
                    fullWidth
                    variant="outlined"
                />
            </Stack>

            <Box>
                <Typography variant="subtitle1" sx={{ mb: 1 }}>
                    Zdjęcie (jedno)
                </Typography>

                <Box
                    onDrop={onDrop}
                    onDragOver={onDragOver}
                    sx={{
                        border: "2px dashed #ccc",
                        borderRadius: 2,
                        p: 2,
                        display: "flex",
                        alignItems: "center",
                        gap: 2,
                        backgroundColor: "#fafafa",
                    }}
                >
                    <Button variant="outlined" onClick={onPickFile}>
                        Wybierz plik
                    </Button>
                    <input
                        ref={fileInputRef}
                        type="file"
                        accept="image/*"
                        onChange={onFileChange}
                        hidden
                    />
                    <Typography variant="body2" sx={{ color: "#666" }}>
                        Przeciągnij i upuść tutaj lub wybierz z dysku
                    </Typography>
                </Box>

                {previewUrl && (
                    <Box sx={{ mt: 2, display: "flex", justifyContent: "center" }}>
                        <img
                            src={previewUrl}
                            alt="podgląd kolekcji"
                            style={{
                                maxHeight: 260,
                                maxWidth: "100%",
                                objectFit: "contain",
                                borderRadius: 8,
                            }}
                            onLoad={() => URL.revokeObjectURL(previewUrl)}
                        />
                    </Box>
                )}
            </Box>

            <Box sx={{ display: "flex", justifyContent: "center" }}>
                <Button
                    variant="contained"
                    onClick={handleSubmit}
                    disabled={loading || !name.trim() || !image || !producerId}
                    sx={{ minWidth: 220, fontWeight: 700 }}
                >
                    {loading ? (
                        <CircularProgress size={22} color="inherit" />
                    ) : (
                        "Dodaj kolekcję"
                    )}
                </Button>
            </Box>

            <Snackbar
                open={snack.open}
                autoHideDuration={3000}
                onClose={() => setSnack((s) => ({ ...s, open: false }))}
                anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
            >
                <Alert
                    onClose={() => setSnack((s) => ({ ...s, open: false }))}
                    severity={snack.severity}
                    variant="filled"
                    sx={{ width: "100%" }}
                >
                    {snack.message}
                </Alert>
            </Snackbar>
        </Card>
    );
};

export default NewCollectionForm;